// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/documents/src/importDocx/postprocess/puppeteer/materializeTabWidths.ts

import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import type { EditorState } from '@tiptap/pm/state';

import { EPS, DOCX_TAB_FLUSH, DOCX_TAB_PERSIST, TAB_NODE_NAME } from './constants';
import {
  getMinDirtyTabPos,
  inlineStartLeft,
  posOfTab,
  putWidthIntoStyle,
  pxFromCssVar,
  quantize,
  tabVarForBlock,
} from './tabHelpers';
import { markSystemTr } from '../../plugins/shared';

// Tabs must be processed sequentially: each tab's x-position depends on the previous tab's width
// being already set on the DOM. Batching the measurements would yield wrong stops for tabs
// sharing a line.
const layoutLeftTabsInBlock = (view: EditorView, block: HTMLElement, minTabPos = 0): void => {
  const spans = Array.from(block.querySelectorAll<HTMLElement>(`span.${TAB_NODE_NAME}`));
  if (!spans.length) return;

  const step = pxFromCssVar(block, tabVarForBlock(block));

  const updates: Array<{ pos: number; width: number }> = [];

  for (const span of spans) {
    if (!span.isConnected) continue;

    const pos = posOfTab(view, span);
    if (pos == null || pos < minTabPos) continue;

    span.style.width = '0px';

    // Recalculate blockLeft each iteration: zeroing tabs can shift inline flow.
    const blockLeft = inlineStartLeft(block);

    const spanLeft = span.getBoundingClientRect().left;
    const x = spanLeft - blockLeft;

    const rel = x % step;
    const nearStart = rel <= EPS;
    const nearStop = step - rel <= EPS;

    const nextStop = (Math.floor(x / step) + 1) * step;
    let width = Math.max(0, nextStop - x);

    if (nearStart || nearStop || width <= EPS) width = step;

    width = quantize(width);

    // Set width immediately so the next tab in this paragraph measures against the updated layout.
    span.style.width = `${width}px`;

    const node = view.state.doc.nodeAt(pos);
    if (node?.type.name === TAB_NODE_NAME) {
      const currentStyle = (node.attrs as any).style;
      const nextStyle = putWidthIntoStyle(currentStyle, width);
      if (currentStyle !== nextStyle) {
        updates.push({ pos, width });
      }
    }
  }

  // Single transaction for all updates avoids a transaction storm.
  if (updates.length) {
    let tr = view.state.tr;
    for (const { pos, width } of updates) {
      const node = tr.doc.nodeAt(pos);
      if (node) {
        const nextStyle = putWidthIntoStyle((node.attrs as any).style, width);
        tr = tr.setNodeMarkup(pos, node.type, { ...node.attrs, style: nextStyle }, node.marks);
      }
    }
    markSystemTr(tr).setMeta(DOCX_TAB_PERSIST, true);
    view.dispatch(tr);
  }
};

// If an edit lands after the last tab in a paragraph, we drop the paragraph entirely so the
// layout pass never mutates DOM upstream of the caret.
export const blocksTouchedByChange = (
  view: EditorView,
  oldState: EditorState,
  newState: EditorState
): Map<HTMLElement, number> => {
  const map = new Map<HTMLElement, number>();
  const start = oldState.doc.content.findDiffStart(newState.doc.content);
  if (start == null) return map;

  const diffEnd = oldState.doc.content.findDiffEnd(newState.doc.content);
  const newEnd = diffEnd ? diffEnd.b : newState.doc.content.size;
  const oldEnd = diffEnd ? diffEnd.a : oldState.doc.content.size;
  // 0 = changed region kept its size, so new positions still index the old doc 1:1; any other
  // value shifts them (and can run past the old doc, where nodeAt throws), so skip the lookup.
  const delta = newEnd - oldEnd;

  newState.doc.nodesBetween(start, newEnd, (node, pos) => {
    if (node.type.name === 'paragraph') {
      const oldNode = delta === 0 ? oldState.doc.nodeAt(pos) : null;
      if (oldNode?.type.name === 'paragraph' && node.eq(oldNode)) {
        return false;
      }

      const minTabPos = getMinDirtyTabPos({
        diffStart: start,
        newParagraph: node,
        oldNode,
        paragraphPos: pos,
      });

      if (minTabPos != null) {
        const dom = view.nodeDOM(pos) as HTMLElement | null;
        if (dom) {
          const existingMinTabPos = map.get(dom);
          map.set(dom, existingMinTabPos == null ? minTabPos : Math.min(existingMinTabPos, minTabPos));
        }
      }

      return false;
    }
    return true;
  });

  return map;
};

type TPluginState = { ver: number; didInitialLoad: boolean; flushRequested: boolean };
const pluginKey = new PluginKey<TPluginState>('docx-tabs-docver');

const getTabLayoutPlugin = (isEditMode: () => boolean): Plugin<TPluginState> => {
  let frame: number | undefined;
  const dirtyBlocks = new Map<HTMLElement, number>();

  const flushLayout = (view: EditorView) => {
    if (frame !== undefined) cancelAnimationFrame(frame);
    frame = undefined;
    if (!isEditMode()) {
      dirtyBlocks.clear();
      return;
    }
    const root = view.dom as HTMLElement;

    for (const [block, minTabPos] of [...dirtyBlocks.entries()]) {
      dirtyBlocks.delete(block);
      if (!block.isConnected || !root.contains(block)) {
        continue;
      }
      layoutLeftTabsInBlock(view, block, minTabPos);
    }
  };

  const requestLayout = (view: EditorView) => {
    if (frame !== undefined) return;
    frame = requestAnimationFrame(() => flushLayout(view));
  };

  const markBlocksDirty = (view: EditorView, oldState: EditorState, newState: EditorState) => {
    const blocks = blocksTouchedByChange(view, oldState, newState);
    if (!blocks.size) return;
    blocks.forEach((minTabPos, block) => {
      const existingMinTabPos = dirtyBlocks.get(block);
      dirtyBlocks.set(block, existingMinTabPos == null ? minTabPos : Math.min(existingMinTabPos, minTabPos));
    });
    requestLayout(view);
  };

  return new Plugin<TPluginState>({
    key: pluginKey,
    state: {
      init() {
        return { ver: 0, didInitialLoad: false, flushRequested: false };
      },
      apply(tr, previous) {
        const val = previous.flushRequested ? { ...previous, flushRequested: false } : previous;
        if (tr.getMeta(DOCX_TAB_FLUSH)) return { ...val, flushRequested: true };
        if (tr.getMeta('docxTabInitialLoad')) {
          return { ...val, didInitialLoad: true };
        }
        if (tr.getMeta(DOCX_TAB_PERSIST)) return val;
        // Heading renumbering doesn't move tab stops; suppress to avoid layout-pass storms.
        if (tr.getMeta('headingNumbering')) return val;
        if (tr.docChanged) {
          return { ...val, ver: val.ver + 1 };
        }
        return val;
      },
    },
    view: () => ({
      update: (v, prevState) => {
        const prevPluginState = pluginKey.getState(prevState);
        const curPluginState = pluginKey.getState(v.state);

        if (curPluginState?.flushRequested) {
          flushLayout(v);
          return;
        }
        if (prevPluginState?.ver === curPluginState?.ver) return;

        // First update after mount only flips the flag; backend already calculated tab widths on initial import.
        if (!curPluginState?.didInitialLoad) {
          const tr = markSystemTr(v.state.tr).setMeta(DOCX_TAB_PERSIST, true);
          v.dispatch(tr.setMeta('docxTabInitialLoad', true));
          return;
        }

        // Comment anchors and content loads can change the model while Viewing. Keep saved widths.
        if (isEditMode()) markBlocksDirty(v, prevState, v.state);
      },
      destroy: () => {
        if (frame !== undefined) cancelAnimationFrame(frame);
        frame = undefined;
        dirtyBlocks.clear();
      },
    }),
  });
};

export default getTabLayoutPlugin;
